import React, { useState, useRef } from 'react';
import { Button, Spinner, Alert, Form, Table, Card, Badge } from 'react-bootstrap';
import { FaFileExcel, FaArrowLeft, FaArrowRight, FaCheckCircle, FaUpload } from 'react-icons/fa';
import { API_BASE } from '../config';

const EXPATORY_FIELDS = [
  { value: 'item',             tKey: 'item',           required: true },
  { value: 'type',             tKey: 'type' },
  { value: 'room',             tKey: 'room' },
  { value: 'container',        tKey: 'storage' },
  { value: 'quantity',         tKey: 'qty' },
  { value: 'cost_cad',         tKey: 'cost' },
  { value: 'condition',        tKey: 'cond' },
  { value: 'year_purchased',   tKey: 'yearPurchased' },
  { value: 'certainty',        tKey: 'cert' },
  { value: 'replacement_link', tKey: 'replacementLink' },
];

// Lowercase column-name → db field auto-detection
const AUTO_MAP = {
  'item': 'item', 'name': 'item', 'nom': 'item', 'product': 'item',
  'description': 'item', 'article': 'item',
  'type': 'type', 'category': 'type', 'catégorie': 'type', 'categorie': 'type',
  'room': 'room', 'pièce': 'room', 'piece': 'room',
  'storage': 'container', 'container': 'container', 'rangement': 'container',
  'qty': 'quantity', 'quantity': 'quantity', 'quantité': 'quantity', 'quantite': 'quantity',
  'cost': 'cost_cad', 'price': 'cost_cad', 'value': 'cost_cad',
  'unit price': 'cost_cad', 'prix unit.': 'cost_cad', 'prix': 'cost_cad',
  'coût': 'cost_cad', 'cout': 'cost_cad',
  'condition': 'condition', 'état': 'condition', 'etat': 'condition',
  'year': 'year_purchased', 'year purchased': 'year_purchased',
  'année': 'year_purchased', 'annee': 'year_purchased',
  'cert.': 'certainty', 'certainty': 'certainty', 'certitude': 'certainty',
  'link': 'replacement_link', 'url': 'replacement_link', 'lien': 'replacement_link',
  'replacement link': 'replacement_link',
};

export default function ImportWizard({ authenticatedFetch, t, onClose, onComplete }) {
  const [step, setStep]             = useState(1);
  const [file, setFile]             = useState(null);
  const [analyzing, setAnalyzing]   = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [columns, setColumns]       = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [totalRows, setTotalRows]   = useState(0);
  const [mapping, setMapping]       = useState({});
  const [importing, setImporting]   = useState(false);
  const [importCount, setImportCount] = useState(0);
  const [importError, setImportError] = useState('');
  const [dragOver, setDragOver]     = useState(false);
  const fileInputRef = useRef(null);

  const analyzeFile = async (selectedFile) => {
    if (!selectedFile.name.match(/\.xlsx?$/i)) {
      setAnalyzeError('Please select an Excel file (.xlsx or .xls).');
      return;
    }
    setFile(selectedFile);
    setAnalyzing(true);
    setAnalyzeError('');

    const fd = new FormData();
    fd.append('file', selectedFile);
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/import/analyze`, {
        method: 'POST', body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Could not read file.');
      }
      const data = await res.json();
      setColumns(data.columns);
      setSampleRows(data.sample_rows);
      setTotalRows(data.total_rows);

      // Auto-detect mapping from column names
      const auto = {};
      for (const col of data.columns) {
        const key = col.toLowerCase().trim();
        if (AUTO_MAP[key]) auto[col] = AUTO_MAP[key];
      }
      setMapping(auto);
      setStep(2);
    } catch (e) {
      setAnalyzeError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) analyzeFile(f);
  };

  const setFieldMapping = (col, field) => {
    setMapping(prev => {
      const next = { ...prev };
      if (!field) {
        delete next[col];
        return next;
      }
      // Ensure one-to-one: clear other columns that had this field
      for (const k of Object.keys(next)) {
        if (next[k] === field && k !== col) delete next[k];
      }
      next[col] = field;
      return next;
    });
  };

  const mappedDbFields = Object.values(mapping).filter(Boolean);
  const canImport = mappedDbFields.includes('item');

  // Preview: apply current mapping to sample rows
  const previewRows = sampleRows.map(rawRow => {
    const out = {};
    for (const [col, field] of Object.entries(mapping)) {
      if (rawRow[col] != null) out[field] = rawRow[col];
    }
    return out;
  }).filter(r => r.item);

  const doImport = async () => {
    if (!canImport) return;
    setImporting(true);
    setImportError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('column_map', JSON.stringify(mapping));
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/import`, {
        method: 'POST', body: fd,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Import failed.');
      }
      const data = await res.json();
      setImportCount(data.count);
      setStep(3);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'var(--bs-body-bg)', zIndex: 2000,
      overflowY: 'auto', display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Header bar ── */}
      <div
        style={{ background: 'linear-gradient(135deg, #1a6b3c 0%, #2e8b57 100%)', color: '#fff', flexShrink: 0 }}
        className="px-3 px-md-4 py-3 d-flex align-items-center gap-3"
      >
        <Button variant="link" className="text-white p-0 text-decoration-none" onClick={onClose}
                style={{ lineHeight: 1 }}>
          <FaArrowLeft size={18} />
        </Button>
        <FaFileExcel size={20} />
        <span className="fw-bold fs-5 mb-0">{t.importWizardTitle}</span>
        {step < 3 && (
          <div className="ms-auto d-flex align-items-center gap-1">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: step >= s ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.28)',
                  color: step >= s ? '#1a6b3c' : 'rgba(255,255,255,0.7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12,
                }}>{s}</div>
                {s < 2 && <div style={{ width: 18, height: 2, background: 'rgba(255,255,255,0.35)' }} />}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ── Page body ── */}
      <div style={{ flex: 1, maxWidth: 860, margin: '0 auto', width: '100%' }}
           className="px-3 px-md-4 py-4">

        {/* ── Step 1: Upload ── */}
        {step === 1 && (
          <>
            <h4 className="fw-bold mb-1">{t.importStep1Title}</h4>
            <p className="text-muted mb-4">{t.importStep1Desc}</p>

            {analyzeError && <Alert variant="danger" className="mb-3">{analyzeError}</Alert>}

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !analyzing && fileInputRef.current.click()}
              className="text-center rounded-4 p-5"
              style={{
                border: `2px dashed ${dragOver ? '#2e8b57' : 'var(--bs-border-color)'}`,
                background: dragOver ? 'rgba(46,139,87,0.06)' : 'transparent',
                cursor: analyzing ? 'default' : 'pointer',
                transition: 'all 0.18s',
              }}
            >
              {analyzing ? (
                <>
                  <Spinner animation="border" variant="success" className="mb-3"
                           style={{ width: 42, height: 42 }} />
                  <p className="text-muted mb-0">{t.importAnalyzing}</p>
                </>
              ) : (
                <>
                  <FaFileExcel size={54} className="text-success mb-3" />
                  <h5 className="mb-1">{t.importDropHere}</h5>
                  <p className="text-muted small mb-4">{t.importOrBrowse}</p>
                  <Button variant="success" size="lg" className="px-5">
                    <FaUpload className="me-2" />{t.import}
                  </Button>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files[0];
                if (f) analyzeFile(f);
                e.target.value = '';
              }}
            />
          </>
        )}

        {/* ── Step 2: Column mapping + preview ── */}
        {step === 2 && (
          <>
            <div className="d-flex justify-content-between align-items-start mb-1">
              <div>
                <h4 className="fw-bold mb-1">{t.importStep2Title}</h4>
                <p className="text-muted mb-0">{t.importStep2Desc}</p>
              </div>
              <Badge bg="secondary" className="flex-shrink-0 ms-3 mt-1" style={{ fontSize: '0.8rem' }}>
                {totalRows} {t.itemsInInventory}
              </Badge>
            </div>
            <hr className="my-3" />

            {/* Mapping table */}
            <Card className="shadow-sm border-0 mb-4">
              <Table responsive className="mb-0 align-middle" style={{ fontSize: '0.9rem' }}>
                <thead style={{ background: 'var(--bs-tertiary-bg)' }}>
                  <tr>
                    <th className="ps-3 fw-semibold py-2">{t.importColFrom}</th>
                    <th className="fw-semibold py-2" style={{ minWidth: 160 }}>{t.importColSample}</th>
                    <th className="pe-3 fw-semibold py-2" style={{ minWidth: 210 }}>{t.importColTo}</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map(col => {
                    const currentField = mapping[col] || '';
                    const takenByOther = mappedDbFields.filter(f => f !== currentField);
                    return (
                      <tr key={col}>
                        <td className="ps-3 py-2 fw-medium" style={{ whiteSpace: 'nowrap' }}>
                          {col}
                        </td>
                        <td className="py-2">
                          {sampleRows.slice(0, 2).map((r, i) => (
                            <span
                              key={i}
                              className="badge me-1 text-truncate d-inline-block"
                              style={{
                                maxWidth: 120, background: 'var(--bs-secondary-bg)',
                                color: 'var(--bs-body-color)', fontWeight: 400, fontSize: '0.78rem',
                              }}
                            >
                              {r[col] ?? '—'}
                            </span>
                          ))}
                        </td>
                        <td className="pe-3 py-2">
                          <Form.Select
                            size="sm"
                            value={currentField}
                            onChange={e => setFieldMapping(col, e.target.value)}
                            style={{ minWidth: 195 }}
                          >
                            <option value="">{t.importSkip}</option>
                            {EXPATORY_FIELDS.map(f => (
                              <option
                                key={f.value}
                                value={f.value}
                                disabled={takenByOther.includes(f.value)}
                              >
                                {t[f.tKey]}{f.required ? ' *' : ''}
                                {takenByOther.includes(f.value) ? ' ✓' : ''}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card>

            {/* Preview section */}
            {previewRows.length > 0 && (
              <div className="mb-4">
                <h6
                  className="text-uppercase fw-semibold mb-2"
                  style={{ fontSize: '0.72rem', letterSpacing: '0.06em', color: 'var(--bs-secondary-color)' }}
                >
                  {t.importPreviewTitle}
                </h6>
                <Card className="shadow-sm border-0">
                  <Table responsive className="mb-0 small align-middle">
                    <thead style={{ background: 'var(--bs-tertiary-bg)' }}>
                      <tr>
                        {EXPATORY_FIELDS.filter(f => mappedDbFields.includes(f.value)).map(f => (
                          <th key={f.value} className="px-3 py-2 fw-semibold">
                            {t[f.tKey]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i}>
                          {EXPATORY_FIELDS.filter(f => mappedDbFields.includes(f.value)).map(f => (
                            <td key={f.value} className="px-3 py-2">
                              {row[f.value] ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </div>
            )}

            {!canImport && (
              <Alert variant="warning" className="mb-3">{t.importNoItemCol}</Alert>
            )}
            {importError && <Alert variant="danger" className="mb-3">{importError}</Alert>}

            <div className="d-flex justify-content-end pb-4">
              <Button
                variant="success"
                size="lg"
                onClick={doImport}
                disabled={!canImport || importing}
                style={{ minWidth: 230 }}
              >
                {importing ? (
                  <><Spinner animation="border" size="sm" className="me-2" />{t.importImporting}</>
                ) : (
                  <>{t.importDoImport.replace('{n}', totalRows)}<FaArrowRight className="ms-2" /></>
                )}
              </Button>
            </div>
          </>
        )}

        {/* ── Step 3: Success ── */}
        {step === 3 && (
          <div className="text-center py-5">
            <FaCheckCircle size={72} className="text-success mb-4" />
            <h3 className="fw-bold mb-2">{t.importStep3Title}</h3>
            <p className="text-muted mb-5 fs-5">
              <strong>{importCount}</strong> {t.importItemsAdded}
            </p>
            <Button variant="success" size="lg" className="px-5" onClick={onComplete}>
              {t.importGoInventory}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
